import * as React from 'react';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../../app/ApplicationContext';
import {ErrorResponse, IncomingInvoice, Partner, User} from '../../api/dtos/dtos';
import {RouteComponentProps, withRouter} from 'react-router';
import {ErrorAlert} from '../../common/ErrorAlert';
import IncomingInvoiceForm from './IncomingInvoiceForm';
import furoApi from '../../api/FuroApi';
import {showDeleteSuccessNotification, showSaveSuccessNotification} from '../../utils/dialogs';
import * as paths from '../../paths';
import {now, toIsoDateString} from '../../utils/date-utils';
import moment from "moment";
import {CURRENCY_EUR} from "../../utils/currency-utils";

interface State {
  types: string[];
  partners: Partner[];
  users: User[];
  eurExchangeRate?: number;
  error?: ErrorResponse;
}

class CreateIncomingInvoicePage extends React.Component<RouteComponentProps<{}>, State> {

  public readonly loadingHandler?: LoadingOverlayHandler;
  public state: State = {
    types: [],
    partners: [],
    users: [],
  };

  public componentDidMount() {
    this.loadInvoiceTypes();
    this.loadUsers();
  }

  public render() {
    const {types, partners, users, error, eurExchangeRate} = this.state;
    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>
        <IncomingInvoiceForm
          types={types}
          editable={true}
          partners={partners}
          users={users.map(user => user.userName!)}
          defaultEurExchangeRate={eurExchangeRate}
          onRefreshDefaultExchangeRate={this.refreshDefaultExchangeRate}
          onPartnerSearch={this.searchPartner}
          onPartnerDelete={this.deletePartner}
          onFormSubmit={this.saveInvoiceForm}
          onFormCancel={this.cancel}
        />
        <ErrorAlert style={{marginTop: '16px'}} error={error}/>
      </>
    );
  }

  private cancel = () => this.props.history.goBack();

  private saveInvoiceForm = (invoice: IncomingInvoice) => {
    const promise = furoApi.incomingInvoices.create(invoice)
      .then(this.onInvoiceSaved)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onInvoiceSaved = (invoice: IncomingInvoice) => {
    showSaveSuccessNotification();
    this.props.history.replace(paths.viewIncomingInvoicePath(invoice.id));
  };

  private loadUsers= () => {
    const promise = furoApi.users.getUsers()
      .then((response) => this.setState({users: response}))
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private loadInvoiceTypes = () => {
    const promise = furoApi.incomingInvoices.getTypes()
      .then((response) => this.setState({types: response.types}))
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private refreshDefaultExchangeRate = (date?: moment.Moment) => {
    if (!date ||date.isAfter(now(), 'day')) {
      return;
    }
    return this.loadCurrentEurExchangeRate(date);
  };

  private loadCurrentEurExchangeRate = (d?: moment.Moment) => {
    const date = toIsoDateString(d ?? now());
    const promise = furoApi.currency.getRate(CURRENCY_EUR, date)
      .then((response) => {
        this.setState({eurExchangeRate: response.rate});
      })
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
    return promise;
  };

  private searchPartner = (value: string) => {
    furoApi.incomingInvoices.searchPartners(value)
      .then((partners) => this.setState({partners}))
      .catch(this.onApiError);
  };

  private deletePartner = (id: number) => {
    furoApi.incomingInvoices.deletePartner(id)
      .then((_) => this.onPartnerDeleted(id))
      .catch(this.onApiError);
  };

  private onPartnerDeleted = (id: number) => {
    showDeleteSuccessNotification();
    const {partners} = this.state;
    const idx = partners.findIndex(p => p.id === id);
    if (idx > -1) {
      partners.splice(idx, 1);
      this.setState({partners});
    }
  };

  private onApiError = (error: ErrorResponse) => this.setState({error});
}

export default withRouter(CreateIncomingInvoicePage);


// WEBPACK FOOTER //
// ./src/incoming-invoices/details/CreateIncomingInvoicePage.tsx